import {inject, Injectable} from '@angular/core';
import {AnalyticsService} from '../shared';
import {FirebaseInitService} from '../shared/firebase-init.service';
import {OnboardingProgress, OnboardingStep, Profile} from '@models';
import {doc, updateDoc} from 'firebase/firestore';
import {ProfileService} from '../community';
import {OnboardingQueryService} from './onboarding-query.service';

@Injectable({
	providedIn: 'root'
})
export class OnboardingCommandService {
	private readonly firebaseInit = inject(FirebaseInitService);
	private readonly profileService = inject(ProfileService);
	private readonly analyticsService = inject(AnalyticsService);
	private readonly onboardingQuery = inject(OnboardingQueryService);
	private readonly profilePath = 'profile';

	async saveForLater(): Promise<void> {
		await this.syncProgress();
	}

	async completeFirstInteraction(source: 'favorite' | 'message'): Promise<void> {
		const currentProfile = this.profileService.profile();
		if (!currentProfile?.uuid) {
			return;
		}
		const state = this.onboardingQuery.getState(currentProfile);
		if (state.completedSteps.includes(OnboardingStep.FirstInteraction)) {
			return;
		}
		const completedSteps = Array.from(new Set([...state.completedSteps, OnboardingStep.FirstInteraction]));
		const isFullyCompleted = completedSteps.length === 3;
		const nextState: OnboardingProgress = {
			...state,
			completedSteps,
			currentStep: isFullyCompleted ? OnboardingStep.FirstInteraction : state.currentStep,
			interactionSource: source,
			completedAt: isFullyCompleted ? Date.now() : undefined,
			lastUpdatedAt: Date.now()
		};
		await this.persistState(currentProfile, nextState, state);
	}

	async syncProgress(): Promise<void> {
		const currentProfile = this.profileService.profile();
		if (!currentProfile?.uuid) {
			return;
		}
		const previousState = currentProfile.getOnboardingProgress();
		const nextState = {
			...this.onboardingQuery.getState(currentProfile),
			lastUpdatedAt: Date.now()
		};
		await this.persistState(currentProfile, nextState, previousState);
	}

	private async persistState(profile: Profile, nextState: OnboardingProgress, previousState: OnboardingProgress): Promise<void> {
		if (!profile.uuid) {
			return;
		}
		profile.onboarding = nextState;
		this.trackStepChanges(previousState, nextState);
		await updateDoc(doc(this.firebaseInit.firestore, this.profilePath, profile.uuid), {
			onboarding: nextState,
			updatedAt: Date.now()
		});
	}

	private trackStepChanges(previousState: OnboardingProgress, nextState: OnboardingProgress): void {
		const previousSteps = new Set(previousState.completedSteps ?? []);
		(nextState.completedSteps ?? []).forEach((step) => {
			if (!previousSteps.has(step)) {
				this.analyticsService.trackEvent('onboarding_step_completed', {step});
			}
		});
		if (!previousState.completedAt && !!nextState.completedAt) {
			this.analyticsService.trackEvent('onboarding_completed', {
				source: nextState.interactionSource ?? 'unknown'
			});
		}
	}
}
